import { type ReactNode } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import StatusBar from '@/designs-dev/device/StatusBar';
import HomeIndicator from '@/designs-dev/device/HomeIndicator';
import TabBar from '@/designs-dev/device/TabBar';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import ScreenCenter from '@/components/wrapped/ScreenCenter';
import AccountBar, { type Account } from '@/karehero-app/access/AccountBar';
import { APP_HEADER_H } from '@/karehero-app/shell/layout';
import { INTAKE_PROCESSING } from '@/karehero-app/tell-us-once/intake/content';

// The end of the intake. Everything they sent is read at once here, rather than
// note by note as it arrived, and they are told instead of made to wait.
//
// No action, deliberately: being pinged is the way out, so a button would either
// lie about what it does or invite them to sit here.
//
// `inApp` is the same message with the app's own chrome around it, sitting on the
// home tab. It is the difference between the intake ending on a screen of its own
// and the intake handing them to the app while it works: with the bars there, not
// waiting is something they can act on rather than just be told.
const ProcessingScreen = ({
  inApp = false,
  account,
  bar,
  title = INTAKE_PROCESSING.title,
  description = INTAKE_PROCESSING.description,
}: {
  inApp?: boolean;
  /** The app bar. See StepScreen's own `account`. */
  account?: Account;
  /** A flow's own bar, for a wait that happens inside one. */
  bar?: ReactNode;
  /**
   * What is being read, and how long it takes. The intake's wording by default.
   *
   * Props rather than a second screen: the calculator reads paperwork here and
   * the intake reads a conversation, but the thing on screen is the same wait,
   * and drawing it twice is how two orbs end up different sizes.
   */
  title?: string;
  description?: string;
}) => (
  <ScreenRoot>
    <StatusBar />
    {bar}
    {account && (
      <AccountBar {...account} onNotifications={inApp ? () => {} : undefined} />
    )}

    {/* Centred against the frame, not against the space under the bar. A bar
        above with only the home indicator below is 60px of chrome the bottom
        does not answer, and this message sat 30px low on every screen of the
        claim because of it. In app the tab bar answers the bar, so there is
        nothing to give back. */}
    <ScreenCenter offset={Boolean(bar || account) && !inApp ? APP_HEADER_H : 0}>
      <Stack sx={{ alignItems: 'center' }} spacing={4}>
        {/* The logo animation, not the orb. The orb is the thing you talk to,
            and this is nobody talking: the reading happens after they have
            gone. Same asset and size as the account-setup wait, so the two
            waits in the app are recognisably the same event. */}
        <Box
          sx={{ width: 64, height: 62 }}
          component='img'
          src='/vector/karehero-loader.svg'
          alt=''
        />
        <Stack spacing={1}>
          {/* h2, not h1: at h1 this ran to three lines and shouted a status.
              The interstitials keep h1 because they are a moment; this is a
              message about work happening elsewhere. */}
          <Typography sx={{ textAlign: 'center' }} variant='h2'>
            {title}
          </Typography>
          <Typography sx={{ textAlign: 'center' }} variant='subtitle1'>
            {description}
          </Typography>
        </Stack>
      </Stack>
    </ScreenCenter>

    {/* Inert, like every other tab bar in this flow: the other tabs are not part
        of this work. It is here to say the app is reachable, not to be used.
        TabBar carries the home indicator, so the bare screen supplies its own and
        this one does not. */}
    {inApp ? <TabBar active='home' /> : <HomeIndicator />}
  </ScreenRoot>
);

export default ProcessingScreen;
